import React, { useState, useRef, useEffect } from 'react';
import { useSermons } from '../../hooks/useSermons';
import { useSermonMedia } from '../../hooks/useSermonMedia';
import {
  Image as ImageIcon,
  Loader2,
  AlertCircle,
  Download,
  Trash2,
  Sparkles,
  Monitor,
  Square,
  Smartphone,
  Upload,
  X,
  BookmarkPlus,
  BookmarkCheck,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface GeneratedImage {
  id: string;
  url: string;
  prompt: string;
  timestamp: number;
  sermonId: string;
}

interface VisualAnalysis {
  visualConcept: string;
  symbolism: string;
  colorPalette: string;
  atmosphere: string;
  composition: string;
}

type ImageVibe = 'clean' | 'warm' | 'bold' | 'watercolor' | 'editorial';
type AspectRatio = '16:9' | '1:1' | '9:16';
type ImageResolution = '1K' | '2K' | '4K';

const RESOLUTIONS: { id: ImageResolution; name: string; description: string }[] = [
  { id: '1K', name: '1K', description: 'Standard' },
  { id: '2K', name: '2K', description: 'High' },
  { id: '4K', name: '4K', description: 'Ultra' }
];

const VIBES: { id: ImageVibe; name: string; description: string }[] = [
  {
    id: 'clean',
    name: 'Clean & Modern',
    description: 'Minimal, spacious, professional',
  },
  {
    id: 'warm',
    name: 'Warm Naturals',
    description: 'Earthy textures, inviting tones',
  },
  {
    id: 'bold',
    name: 'Bold & Graphic',
    description: 'Strong shapes, high contrast',
  },
  {
    id: 'watercolor',
    name: 'Soft Watercolor',
    description: 'Painted washes, organic feel',
  },
  {
    id: 'editorial',
    name: 'Magazine Editorial',
    description: 'Collage textures, layered depth',
  },
];

const VIBE_STYLE_PROMPTS: Record<ImageVibe, string> = {
  clean: `DESIGN STYLE:
- Clean, modern, minimal aesthetic — generous intentional negative space
- Soft geometric accents: thin lines, simple abstract shapes, subtle gradient panels
- Sophisticated 2–3 color palette: neutrals plus one strong accent color
- No heavy textures, torn edges, or collage elements
- Feels like it was designed by a professional church creative team
- Contemporary sermon slide / Sunday bulletin aesthetic`,

  warm: `DESIGN STYLE:
- Warm, earthy, organic aesthetic
- Soft textured backgrounds: aged paper, linen, light wood grain, or brushed canvas
- Warm color palette: cream, tan, terracotta, warm white, olive, or dusty rose
- Subtle botanical accents: simple leaves, vines, or organic shapes as peripheral details
- Handcrafted feel — like a thoughtfully designed church bulletin cover
- Approachable, community-focused, welcoming`,

  bold: `DESIGN STYLE:
- Bold, graphic, high-impact design
- Strong color blocks and sharp geometric shapes as compositional anchors
- High contrast between background and accent elements
- Dynamic diagonal lines or angular shapes add energy
- Contemporary and youth-friendly without being loud or chaotic
- Feels like premium brand design — clean edges, intentional color choices`,

  watercolor: `DESIGN STYLE:
- Soft watercolor or gouache wash aesthetic
- Painterly backgrounds with diffused, organic color blending
- Pastel to mid-tone palette: sky blue, lavender, blush, sage green, or warm ivory
- Soft feathered edges and gentle transitions between color areas
- Delicate and inviting — appropriate for all ages and contexts
- Feels handmade and personal, like an illustrated journal cover`,

  editorial: `DESIGN STYLE:
- Mixed media collage aesthetic with layered textures
- Torn paper edges and overlapping texture fragments throughout
- Gold foil brushstrokes and subtle metallic accents
- Hand-drawn marks, ink splatters, and abstract gestural shapes
- Crumpled kraft paper or tissue paper textures as background elements
- Vintage grayscale or sepia photography fragments related to the theme`,
};

const ASPECT_RATIOS: { id: AspectRatio; name: string; icon: React.ReactNode; use: string }[] = [
  { id: '16:9', name: 'Widescreen', icon: <Monitor className="w-4 h-4" />, use: 'Slides & Screens' },
  { id: '1:1', name: 'Square', icon: <Square className="w-4 h-4" />, use: 'Social Media' },
  { id: '9:16', name: 'Vertical', icon: <Smartphone className="w-4 h-4" />, use: 'Stories & Reels' }
];

const AI_SECTION_ID = 'ai-generated';

export function SermonImageGenerator({ defaultSermonId }: { defaultSermonId?: string } = {}) {
  const { sermons } = useSermons();
  const [selectedSermonId, setSelectedSermonId] = useState(defaultSermonId ?? '');
  const [generating, setGenerating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [contentWarning, setContentWarning] = useState<string | null>(null);
  const [showShortContentConfirm, setShowShortContentConfirm] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [selectedVibe, setSelectedVibe] = useState<ImageVibe>('clean');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('16:9');
  const [imageResolution, setImageResolution] = useState<ImageResolution>('2K');
  const [includeTitle, setIncludeTitle] = useState(false);
  const [analysisCache, setAnalysisCache] = useState<Record<string, VisualAnalysis>>({});
  const [styleNotes, setStyleNotes] = useState('');
  const [styleReferenceImage, setStyleReferenceImage] = useState<string | null>(null);
  const [styleReferencePreview, setStyleReferencePreview] = useState<string | null>(null);
  const [savedImageIds, setSavedImageIds] = useState<Set<string>>(new Set());
  const [savingImageIds, setSavingImageIds] = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { media, uploadFile, deleteFile, getPublicUrl, loadMedia } = useSermonMedia(selectedSermonId);

  const savedGraphics = media.filter(m => m.section_id === AI_SECTION_ID);

  const selectedSermon = sermons.find(s => s.id === selectedSermonId);

  // Reload saved graphics when sermon selection changes
  useEffect(() => {
    if (selectedSermonId) loadMedia(selectedSermonId);
  }, [selectedSermonId]);

  const handleStyleReferenceUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setGenerationError('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setGenerationError('Image must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setStyleReferenceImage(base64.split(',')[1]);
      setStyleReferencePreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const removeStyleReference = () => {
    setStyleReferenceImage(null);
    setStyleReferencePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const sanitizeContent = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const analyzeSermonContent = async (sermonId: string, content: string, title: string, scripture: string): Promise<VisualAnalysis | null> => {
    if (analysisCache[sermonId]) {
      return analysisCache[sermonId];
    }

    try {
      setAnalyzing(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Please sign in');

      const cleanContent = sanitizeContent(content);
      const maxContentLength = 5000;
      const truncatedContent = cleanContent.length > maxContentLength
        ? cleanContent.slice(0, maxContentLength) + '...'
        : cleanContent;

      const analysisPayload = `SERMON TITLE: ${title}
SCRIPTURE: ${scripture}

SERMON CONTENT:
${truncatedContent}`;

      const analysisController = new AbortController();
      const analysisTimeout = setTimeout(() => analysisController.abort(), 30000);

      let response: Response;
      try {
        response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-analysis`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            type: 'analyzeImage',
            content: analysisPayload
          }),
          signal: analysisController.signal
        });
      } finally {
        clearTimeout(analysisTimeout);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Analysis failed');
      }

      const analysis: VisualAnalysis = JSON.parse(data.content);

      setAnalysisCache(prev => ({ ...prev, [sermonId]: analysis }));

      return analysis;
    } catch (error) {
      console.error('Analysis error:', error);
      return null;
    } finally {
      setAnalyzing(false);
    }
  };

  const buildPrompt = (analysis: VisualAnalysis | null, title: string, scripture: string, vibe: ImageVibe, withTitle: boolean, customStyleNotes: string): string => {
    const hasCustomStyle = customStyleNotes.trim().length > 0;
    const hasStyleRef = !!styleReferenceImage;
    const hasUserDirection = hasCustomStyle || hasStyleRef;

    const textRequirements = withTitle
      ? `TYPOGRAPHY - THE MAIN FOCAL POINT:
- Display the title "${title}" as large, bold, stylized typography
- Use elegant serif fonts like Playfair Display, Freight Text, or classic editorial typefaces
- Typography should be THE HERO of the composition — everything else supports it
- Scripture reference "${scripture}" in smaller elegant text nearby
- Text should feel intentionally placed and part of the design, not an afterthought
- NO people, faces, hands, or human figures anywhere`
      : `NO TEXT — COMPOSITION MUST HAVE A LARGE OPEN CENTER FOR TEXT OVERLAY:
- Do NOT include any actual text, words, letters, or characters
- COMPOSITION RULE: Treat this as a background for a title slide — decorative elements, shapes, and visual details must stay in the outer 25% border/frame of the image
- The center and middle area (at least 60% of the image) MUST be a clean, open surface — solid color, subtle gradient, soft texture, or gently blurred background
- NOTHING should cross into or obstruct the center area — no objects, shapes, or design elements
- The center area needs strong contrast against any border elements so overlaid text will be highly readable
- NO people, faces, hands, or human figures anywhere`;

    const userStyleSection = hasCustomStyle
      ? `\nHIGHEST PRIORITY - USER STYLE DIRECTIONS (follow these above all other instructions):\n${customStyleNotes.trim()}\n`
      : '';

    const vibeStyle = VIBE_STYLE_PROMPTS[vibe];

    if (analysis && analysis.visualConcept) {
      return `Create a sermon graphic for "${title}" (${scripture}).
${userStyleSection}
VISUAL CONCEPT FROM SERMON: ${analysis.visualConcept}
SYMBOLIC MEANING: ${analysis.symbolism}
SUGGESTED COLORS: ${analysis.colorPalette}
ATMOSPHERE: ${analysis.atmosphere}
${hasUserDirection ? '' : `\n${vibeStyle}\n`}
${textRequirements}`;
    }

    return `Create a sermon graphic for "${title}" (${scripture}).
${userStyleSection}
${hasUserDirection ? '' : `${vibeStyle}\n`}
${textRequirements}`;
  };

  const checkContentAndGenerate = () => {
    if (!selectedSermon) {
      setGenerationError('Please select a sermon first');
      return;
    }

    const sermonContent = selectedSermon.content || '';
    const cleanText = sanitizeContent(sermonContent).trim();

    if (cleanText.length < 100) {
      setShowShortContentConfirm(true);
    } else {
      generateImage(false);
    }
  };

  const generateImage = async (skipAnalysis = false) => {
    if (!selectedSermon) {
      setGenerationError('Please select a sermon first');
      return;
    }

    setShowShortContentConfirm(false);

    try {
      setGenerating(true);
      setGenerationError(null);
      setContentWarning(null);

      const sermonContent = selectedSermon.content || '';
      const cleanText = sanitizeContent(sermonContent).trim();
      const hasEnoughContent = cleanText.length >= 100;

      let analysis: VisualAnalysis | null = null;

      if (hasEnoughContent && !skipAnalysis) {
        analysis = await analyzeSermonContent(
          selectedSermon.id,
          sermonContent,
          selectedSermon.title,
          selectedSermon.scripture
        );

        if (!analysis) {
          setContentWarning('Could not analyze sermon content. Using title and scripture for image generation.');
        }
      } else if (!hasEnoughContent) {
        setContentWarning('Using title and scripture only for this image.');
      }

      const prompt = buildPrompt(analysis, selectedSermon.title, selectedSermon.scripture, selectedVibe, includeTitle, styleNotes);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Please sign in to generate images');

      const requestBody: Record<string, unknown> = {
        prompt,
        imageStyle: selectedVibe,
        aspectRatio,
        imageSize: imageResolution
      };

      if (styleReferenceImage) {
        requestBody.styleReferenceImage = styleReferenceImage;
      }

      const imageController = new AbortController();
      const imageTimeout = setTimeout(() => imageController.abort(), 90000);

      let imageResponse: Response;
      try {
        imageResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-image-generation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
          },
          body: JSON.stringify(requestBody),
          signal: imageController.signal
        });
      } finally {
        clearTimeout(imageTimeout);
      }

      const data = await imageResponse.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to generate image');
      }

      const imageUrl = Array.isArray(data.content) ? data.content[0]?.url : data.content?.url;

      if (!imageUrl) {
        throw new Error('No image was generated. Please try again.');
      }

      const newImage: GeneratedImage = {
        id: Math.random().toString(36).substring(2, 15),
        url: imageUrl,
        prompt,
        timestamp: Date.now(),
        sermonId: selectedSermon.id,
      };

      setGeneratedImages(prev => [newImage, ...prev]);

    } catch (error) {
      console.error('Error generating image:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        setGenerationError('Image generation timed out. Please try again.');
      } else {
        setGenerationError(error instanceof Error ? error.message : 'Failed to generate image');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (image: GeneratedImage, index: number) => {
    try {
      if (image.url.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = image.url;
        link.download = `${selectedSermon?.title || 'sermon'}-graphic-${index + 1}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const response = await fetch(image.url);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${selectedSermon?.title || 'sermon'}-graphic-${index + 1}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Download failed:', error);
      setGenerationError('Failed to download. Try right-clicking the image and selecting "Save image as..."');
    }
  };

  const handleSaveToSermon = async (image: GeneratedImage) => {
    if (!image.sermonId) return;
    setSaveError(null);
    setSavingImageIds(prev => new Set(prev).add(image.id));

    try {
      const response = await fetch(image.url);
      if (!response.ok) throw new Error('Failed to fetch image for saving');
      const blob = await response.blob();

      const sermon = sermons.find(s => s.id === image.sermonId);
      const safeName = (sermon?.title || 'sermon').replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const fileName = `${safeName}-graphic-${Date.now()}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });

      const result = await uploadFile({
        sermonId: image.sermonId,
        sectionId: AI_SECTION_ID,
        file,
      });

      if (result) {
        setSavedImageIds(prev => new Set(prev).add(image.id));
        await loadMedia(image.sermonId);
      } else {
        throw new Error('Upload returned null');
      }
    } catch (err) {
      console.error('Failed to save image:', err);
      setSaveError('Could not save the image. Please try again.');
    } finally {
      setSavingImageIds(prev => {
        const next = new Set(prev);
        next.delete(image.id);
        return next;
      });
    }
  };

  const handleDelete = (id: string) => {
    setGeneratedImages(prev => prev.filter(img => img.id !== id));
  };

  const isLoading = generating || analyzing;
  const loadingText = analyzing ? 'Analyzing sermon...' : 'Generating graphic...';

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-charcoal rounded-lg shadow-sm border border-sand/20 dark:border-charcoal/60 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-electric/10 rounded-lg">
            <ImageIcon className="w-5 h-5 text-electric" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-midnight dark:text-soft-white">Sermon Graphics Generator</h2>
            <p className="text-sm text-midnight/60 dark:text-soft-white/60">Create title slides and social graphics based on your sermon content</p>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-midnight dark:text-soft-white mb-2">
              Select Sermon
            </label>
            <select
              value={selectedSermonId}
              onChange={(e) => setSelectedSermonId(e.target.value)}
              className="w-full px-4 py-3 border border-sand/30 dark:border-charcoal/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric/20 text-midnight dark:text-soft-white dark:bg-midnight/30"
            >
              <option value="">Choose a sermon...</option>
              {sermons.map(sermon => (
                <option key={sermon.id} value={sermon.id}>
                  {sermon.title} {sermon.scripture && `(${sermon.scripture})`}
                </option>
              ))}
            </select>
          </div>

          {selectedSermon && (
            <div className="p-4 bg-sand/10 dark:bg-midnight/30 rounded-lg">
              <p className="font-medium text-midnight dark:text-soft-white">{selectedSermon.title}</p>
              {selectedSermon.scripture && (
                <p className="text-sm text-midnight/60 dark:text-soft-white/60">{selectedSermon.scripture}</p>
              )}
              {analysisCache[selectedSermon.id] && (
                <div className="flex items-center gap-2 mt-2">
                  <p className="text-xs text-electric">Visual concept ready</p>
                  <button
                    onClick={() => setAnalysisCache(prev => { const next = { ...prev }; delete next[selectedSermon.id]; return next; })}
                    className="text-xs text-midnight/40 hover:text-crimson underline"
                  >
                    Re-analyze
                  </button>
                </div>
              )}

              {/* Previously saved graphics for this sermon */}
              {savedGraphics.length > 0 && (
                <div className="mt-3 pt-3 border-t border-sand/20 dark:border-charcoal/40">
                  <p className="text-xs font-medium text-slate dark:text-soft-white/70 mb-2">
                    {savedGraphics.length} saved graphic{savedGraphics.length !== 1 ? 's' : ''} for this sermon
                  </p>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {savedGraphics.map(file => (
                      <div key={file.id} className="relative shrink-0 group">
                        <img
                          src={getPublicUrl(file.file_path)}
                          alt="Saved graphic"
                          className="h-16 w-auto rounded-md border border-sand/20 dark:border-charcoal/40 object-cover"
                        />
                        <button
                          onClick={() => deleteFile(file.id)}
                          className="absolute -top-1.5 -right-1.5 hidden group-hover:flex items-center justify-center h-4 w-4 bg-crimson text-white rounded-full"
                          title="Remove saved graphic"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-midnight dark:text-soft-white mb-3">
              Choose a Vibe
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {VIBES.map(vibe => (
                <button
                  key={vibe.id}
                  onClick={() => setSelectedVibe(vibe.id)}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    selectedVibe === vibe.id
                      ? 'border-electric bg-electric/5'
                      : 'border-sand/30 dark:border-charcoal/60 hover:border-electric/30'
                  }`}
                >
                  <p className="font-medium text-midnight dark:text-soft-white text-sm">{vibe.name}</p>
                  <p className="text-xs text-midnight/50 dark:text-soft-white/50 mt-1">{vibe.description}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-midnight dark:text-soft-white mb-3">
                Size
              </label>
              <div className="flex gap-3 flex-wrap">
                {ASPECT_RATIOS.map(ratio => (
                  <button
                    key={ratio.id}
                    onClick={() => setAspectRatio(ratio.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                      aspectRatio === ratio.id
                        ? 'border-electric bg-electric/5'
                        : 'border-sand/30 dark:border-charcoal/60 hover:border-electric/30'
                    }`}
                  >
                    {ratio.icon}
                    <div className="text-left">
                      <p className="text-sm font-medium text-midnight dark:text-soft-white">{ratio.name}</p>
                      <p className="text-xs text-midnight/50 dark:text-soft-white/50">{ratio.use}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-midnight dark:text-soft-white mb-3">
                Resolution
              </label>
              <div className="flex gap-3 flex-wrap">
                {RESOLUTIONS.map(res => (
                  <button
                    key={res.id}
                    onClick={() => setImageResolution(res.id)}
                    className={`px-4 py-2 rounded-lg border-2 transition-all ${
                      imageResolution === res.id
                        ? 'border-electric bg-electric/5'
                        : 'border-sand/30 dark:border-charcoal/60 hover:border-electric/30'
                    }`}
                  >
                    <p className="text-sm font-medium text-midnight dark:text-soft-white">{res.name}</p>
                    <p className="text-xs text-midnight/50 dark:text-soft-white/50">{res.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-sand/10 dark:bg-midnight/30 rounded-lg">
            <div>
              <p className="font-medium text-midnight dark:text-soft-white text-sm">Include sermon title in image</p>
              <p className="text-xs text-midnight/50 dark:text-soft-white/50 mt-0.5">{includeTitle ? 'Title text will be part of the graphic' : 'A placeholder area for text will be designed into the image'}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={includeTitle}
                onChange={(e) => setIncludeTitle(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-sand/40 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-electric/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-electric"></div>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-midnight dark:text-soft-white mb-2">
              Style Reference Image (Optional)
            </label>
            <p className="text-xs text-midnight/50 dark:text-soft-white/50 mb-3">
              Upload an image to use as a style reference. The AI will try to match its colors, textures, and overall feel.
            </p>

            {styleReferencePreview ? (
              <div className="relative inline-block">
                <img
                  src={styleReferencePreview}
                  alt="Style reference"
                  className="w-32 h-32 object-cover rounded-lg border border-sand/30"
                />
                <button
                  onClick={removeStyleReference}
                  className="absolute -top-2 -right-2 p-1 bg-crimson text-white rounded-full hover:bg-crimson/90 transition-colors"
                  type="button"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-sand/40 dark:border-charcoal/60 rounded-lg hover:border-electric/40 transition-colors text-midnight/60 dark:text-soft-white/60 hover:text-midnight dark:hover:text-soft-white"
                type="button"
              >
                <Upload className="w-5 h-5" />
                <span className="text-sm">Upload reference image</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleStyleReferenceUpload}
              className="hidden"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-midnight dark:text-soft-white mb-2">
              Additional Style Notes (Optional)
            </label>
            <textarea
              value={styleNotes}
              onChange={(e) => setStyleNotes(e.target.value)}
              placeholder="Add specific style directions, e.g., 'Use purple and gold colors', 'Make it look like stained glass', 'Add a cross silhouette'..."
              className="w-full px-4 py-3 border border-sand/30 dark:border-charcoal/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric/20 text-midnight dark:text-soft-white dark:bg-midnight/30 resize-none"
              rows={3}
            />
          </div>

          {generationError && (
            <div className="flex items-center gap-2 p-4 bg-crimson/10 text-crimson rounded-lg">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{generationError}</p>
            </div>
          )}

          {saveError && (
            <div className="flex items-center gap-2 p-4 bg-crimson/10 text-crimson rounded-lg">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{saveError}</p>
            </div>
          )}

          {contentWarning && !generationError && (
            <div className="flex items-center gap-2 p-4 bg-gold/10 text-gold rounded-lg">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{contentWarning}</p>
            </div>
          )}

          {showShortContentConfirm && (
            <div className="p-4 bg-gold/10 border border-gold/30 rounded-lg space-y-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-midnight dark:text-soft-white">Limited Content Detected</p>
                  <p className="text-sm text-midnight/70 dark:text-soft-white/70 mt-1">
                    This sermon doesn't have enough content for deep analysis. The image will be based on the title and scripture reference only.
                  </p>
                  <p className="text-sm text-midnight/70 dark:text-soft-white/70 mt-2">
                    For better results, add more content to your sermon first.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowShortContentConfirm(false)}
                  className="px-4 py-2 text-sm text-midnight/70 dark:text-soft-white/70 hover:text-midnight transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => generateImage(true)}
                  className="px-4 py-2 text-sm bg-electric text-white rounded-lg hover:bg-electric/90 transition-colors"
                >
                  Generate Anyway
                </button>
              </div>
            </div>
          )}

          <button
            onClick={checkContentAndGenerate}
            disabled={isLoading || !selectedSermonId || showShortContentConfirm}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {loadingText}
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Graphic
              </>
            )}
          </button>

          <p className="text-xs text-midnight/40 dark:text-soft-white/40 text-center">
            The AI will analyze your sermon content to create a visual concept that matches your message.
          </p>
        </div>
      </div>

      {generatedImages.length > 0 && (
        <div className="bg-white dark:bg-charcoal rounded-lg shadow-sm border border-sand/20 dark:border-charcoal/60 p-6">
          <h3 className="text-lg font-semibold text-midnight dark:text-soft-white mb-4">
            Generated Graphics ({generatedImages.length})
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {generatedImages.map((image, index) => {
              const isSaved = savedImageIds.has(image.id);
              const isSaving = savingImageIds.has(image.id);
              const isCurrentSermon = image.sermonId === selectedSermonId;

              return (
                <div
                  key={image.id}
                  className="relative group rounded-lg overflow-hidden border border-sand/20 dark:border-charcoal/60"
                >
                  <img
                    src={image.url}
                    alt={`Generated graphic ${index + 1}`}
                    className="w-full h-auto"
                  />

                  {/* Saved badge */}
                  {isSaved && (
                    <div className="absolute top-2 left-2 flex items-center gap-1 bg-emerald text-white text-[10px] font-semibold px-2 py-1 rounded-full shadow-sm">
                      <BookmarkCheck className="w-3 h-3" />
                      Saved
                    </div>
                  )}

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                    <button
                      onClick={() => handleDownload(image, index)}
                      className="p-3 bg-white rounded-full hover:bg-sand/10 transition-colors"
                      title="Download"
                    >
                      <Download className="w-5 h-5 text-midnight" />
                    </button>

                    {isCurrentSermon && !isSaved && (
                      <button
                        onClick={() => handleSaveToSermon(image)}
                        disabled={isSaving}
                        className="p-3 bg-white rounded-full hover:bg-electric/10 transition-colors disabled:opacity-60"
                        title="Save to sermon"
                      >
                        {isSaving
                          ? <Loader2 className="w-5 h-5 text-electric animate-spin" />
                          : <BookmarkPlus className="w-5 h-5 text-electric" />
                        }
                      </button>
                    )}

                    <button
                      onClick={() => handleDelete(image.id)}
                      className="p-3 bg-white rounded-full hover:bg-crimson/10 transition-colors"
                      title="Remove from session"
                    >
                      <Trash2 className="w-5 h-5 text-crimson" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-midnight/40 dark:text-soft-white/40 text-center mt-3">
            Use the <BookmarkPlus className="w-3 h-3 inline" /> button on hover to save a graphic to the sermon for later access.
          </p>
        </div>
      )}
    </div>
  );
}
